/**
 * Sandbox route — /sandbox/*
 *
 * Fully standalone entry point with zero auth required.
 * Wraps all sandbox sub-routes in SandboxSessionProvider.
 * On mount, fetches the published sandbox config from the backend
 * and caches it in localStorage for the assessment to consume.
 */

import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { API_BASE } from '../platform';
import SandboxSessionProvider from './SandboxSessionProvider';
import SandboxAssessment from './SandboxAssessment';
import { SANDBOX_CONFIG_KEY } from '../config/zeroTrustConstants';
import '../components/ZeroTrust/ZeroTrust.css';
import './sandbox.css';

/**
 * Fetch the published sandbox config from backend on first load.
 * This ensures the sandbox always has the latest published framework
 * even on a new device/browser (cross-device sync).
 */
function useSandboxConfigLoader() {
  useEffect(() => {
    // Only fetch if no cached config or it's older than 1 hour
    const cached = localStorage.getItem(SANDBOX_CONFIG_KEY);
    let shouldFetch = !cached;
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const age = Date.now() - (parsed.version || 0);
        shouldFetch = age > 60 * 60 * 1000; // 1 hour
      } catch {
        shouldFetch = true;
      }
    }

    if (shouldFetch) {
      fetch(`${API_BASE}/api/zero-trust/sandbox-config`)
        .then(res => res.ok ? res.json() : null)
        .then(config => {
          if (config) {
            localStorage.setItem(SANDBOX_CONFIG_KEY, JSON.stringify(config));
          }
        })
        .catch(() => {
          // Backend unavailable — use whatever's cached or fall back to defaults
        });
    }
  }, []);
}

export default function SandboxRoot() {
  useSandboxConfigLoader();

  return (
    <SandboxSessionProvider>
      <div className="sb-root">
        <Routes>
          <Route index element={<SandboxAssessment />} />
          <Route path="assess" element={<SandboxAssessment />} />
          <Route path="*" element={<Navigate to="/sandbox" replace />} />
        </Routes>
      </div>
    </SandboxSessionProvider>
  );
}
